/**
 * モーダルポップアップ
 */
export class ModalPopup {
  /**
   * 対象の要素オブジェクト
   */
  private readonly $element: JQuery;

  /**
   * コンストラクタ
   * @param element 対象の要素オブジェクト
   * @param setting オプション
   */
  constructor(element: string, setting: ModalPopupOption = {
    duration: 300,
    padding: 10,
    layerName: 'fn-modalPopup__layer',
    targetName: 'fn-modalPopup__target',
    closeName: 'fn-modalPopup__close',
    closeMarkup: ''
  }) {
    this.$element = $(element);
    this.$element.each((index: number, element: HTMLElement): void => {
      const $target: JQuery = $(element);
      $target.data('modal_popup', new ModalPopupObject($target, index, setting));
    });
  }

  /**
   * トリガーオブジェクトを返す
   * @returns トリガーオブジェクト
   */
  public getElement(): JQuery {
    return this.$element;
  }
}

/**
 * オプション
 */
export interface ModalPopupOption {
  /**
   * アニメーションスピード
   */
  duration: number;

  /**
   * ウィンドウサイズとコンテンツ間の余白
   */
  padding: number;

  /**
   * 背景に敷くモーダルレイヤーのクラス名
   */
  layerName: string;

  /**
   * モーダルターゲットコンテンツのクラス名
   */
  targetName: string;

  /**
   * 閉じるボタンのクラス名
   */
  closeName: string;

  /**
   * 閉じるボタンのマークアップ
   */
  closeMarkup: string;
}

/**
* 実行クラス
*/
class ModalPopupObject {
  /**
   * オプション
   */
  private readonly setting: ModalPopupOption;

  /**
   * 要素リスト
   */
  private readonly el: any;

  /**
   * 設定オプション
   */
  private readonly state: any;

  /**
   * コンストラクタ
   * @param element 対象の要素オブジェクト
   * @param index モーダルのインデックス
   * @param setting オプション
   */
  constructor(element: JQuery, index: number, setting: ModalPopupOption) {
    this.el = {};
    this.state = {};
    this.setting = setting;
    this.el.$root = element;
    this.state.id = ('00' + (index + 1)).slice(-2);
    this.state.contentType = (this.el.$root.attr('href').match(/^#.*?$/g)) ? 'internal' : 'external';
    this.state.isOpen = false;
    this.init();
    this.setEvent();
  }

  /**
   * 初期化
   */
  private init(): void {
    this.el.$win = $(window);
    this.el.$body = $('body');
    this.el.$layer = this.el.$body.find('.' + this.setting.layerName);
    this.el.$target = (this.state.contentType === 'internal') ?
      this.el.$body.find('.' + this.setting.targetName).filter(this.el.$root.attr('href')) : null;
    this.el.$targetInner = (this.state.contentType === 'internal') ?
      this.el.$target.find('.' + this.setting.targetName + 'Inner') : null;
    this.createDom();
    this.setAttr();
  }

  /**
   * DOMを作成
   */
  private createDom(): void {
    let dom: any;

    if (! this.el.$layer.length) {
      dom = '';
      dom += `<div class="${this.setting.layerName}"></div>`;
      this.el.$body.append(dom);
      this.el.$layer = this.el.$body.children('.' + this.setting.layerName).hide();
    }

    // ターゲットコンテンツ
    if (this.state.contentType === 'external') {
      dom = '';
      dom += `<div id="modalPopup-${this.state.id}-target" class="${this.setting.targetName}">`;
      dom += `<div class="${this.setting.targetName}Inner">`;
      dom += '</div>';
      dom += '</div>';
      this.el.$body.append(dom);
      this.el.$target = this.el.$body.children('.' + this.setting.targetName).filter(`#modalPopup-${this.state.id}-target`);
      this.el.$targetInner = this.el.$target.children(`.${this.setting.targetName}Inner`);

      // コンテンツを反映する（画像とそれ以外で処理を分ける）
      if (this.el.$root.attr('href').match(/^.*?\.(jpg|gif|png|svg)$/g)) {
        dom = $('<img />');
        dom.attr('src', this.el.$root.attr('href'));
        this.el.$targetInner.append(dom);
      } else {
        this.getContent(this.el.$root.attr('href')).done((data: JQuery): void => {
          this.el.$targetInner.append(data);
        });
      }
    }

    // 閉じるボタン
    if (! $(this.el.$root.attr('href') + ' .' + this.setting.closeName)[0]) {
      dom = '';
      dom += `<button type="button" class="${this.setting.closeName}">`;
      dom += this.setting.closeMarkup;
      dom += '</button>';
      this.el.$target.prepend(dom);
    }

    this.el.$close = this.el.$target.children('.' + this.setting.closeName);
  }

  /**
   * コンテンツ取得(Ajax)
   * @param url URL
   * @returns JQueryXHRオブジェクト
   */
  private getContent(url: string): JQueryXHR {
    return $.ajax({
      url: url
    });
  }

  /**
   * 属性の設定
   */
  private setAttr(): void {
    // モーダルリンク
    this.el.$root.attr({
      'id': `modalPopup-${this.state.id}-btn`,
      'aria-controls': (this.state.contentType === 'internal') ? this.el.$root.attr('href').replace(/^#(.*?)$/g, '$1') : `modalPopup-${this.state.id}-target`
    });

    // モーダルターゲット
    this.el.$target.attr({
      'role': 'dialog',
      'aria-label-ledby': `modalPopup-${this.state.id}-btn`,
      'aria-hidden': true
    });
  }

  /**
   * 全体のtabindexの操作
   * @parem key キー
   */
  private onChangeTabindex(key: string): void {
    let el: JQuery | null = null;

    if (key === 'MODAL_OPEN') {
      el = $('a, button, input, textarea').filter(':visible');

      for (let i: number = 0; i < el.length; i++) {
        // モーダルターゲット内のフォーカス可能要素は操作しない
        if (el.eq(i).parents('.' + this.setting.targetName).length) {
          continue;
        }

        // tabindexを持つエレメントは元のものをdata-popupModal-tabindexに記憶させる
        if (el.eq(i).attr('tabindex')) {
          el.eq(i).filter('[tabindex]').attr({
            'tabindex': '-1',
            'data-popupModal': 'tabindex',
            'data-popupModal-tabindex': el.eq(i).attr('tabindex')
          });
        } else {
          el.eq(i).not('[tabindex]').attr({
            'tabindex': '-1',
            'data-popupModal': 'tabindex'
          });
        }
      }
    } else {
      el = $('a, button, input, textarea').filter('[data-popupModal=tabindex]');

      for (let i = 0; i < el.length; i++) {
        // data-popupModal-tabindexを持つエレメントはtabindexを設定し直す
        if (el.eq(i).attr('data-popupModal-tabindex')) {
          el.eq(i)
            .attr({
              'tabindex': el.eq(i).attr('data-popupModal-tabindex')
            })
            .removeAttr('data-popupModal data-popupModal-tabindex');
        } else {
          el.eq(i).removeAttr('tabindex data-popupModal data-popupModal-tabindex');
        }
      }
    }
  }

  /**
   * モーダルを開く
   * @param event イベントオブジェクト
   */
  private onModalOpen = (event: JQueryEventObject): void => {
    this.state.isOpen = true; // ステータスの更新
    this.el.$layer.show(); // レイヤーの表示
    // ターゲットコンテンツの表示
    this.el.$target
      .fadeIn(300)
      .addClass('is-open')
      .attr({
        'aria-hidden': false
      });
    this.onChangeTabindex('MODAL_OPEN'); // tabindexの操作
    this.adjustStyle(); // スタイルの調整
    this.el.$root.trigger('modal_open'); // イベント送出
    this.state.scrollTop = $(window).scrollTop();
  }

  /**
   * モーダルを閉じる
   * @param event イベントオブジェクト
   */
  private onModalClose = (event: JQueryEventObject): void => {
    if (! this.el.$target.is(':visible')) {
      return;
    }
    // $('html, body').animate({
    //   scrollTop: this.state.scrollTop
    // }, 300);
    this.state.isOpen = false; // ステータスの更新
    this.el.$layer.hide(); // レイヤーの非表示
    // ターゲットコンテンツの非表示
    this.el.$target
      .fadeOut(300)
      .removeClass('is-open')
      .attr({
        'aria-hidden': true
      });
    this.onChangeTabindex('MODAL_CLOSE'); // tabindexの操作
    this.el.$root.trigger('modal_close'); // イベント送出
  }

  /**
   * スタイルの調整
   */
  private adjustStyle(): void {
    // ウィンドウサイズ
    const winSize: {width: number, height: number} = {
      width: (window.innerWidth) ? window.innerWidth : this.el.$win.width(),
      height: (window.innerHeight) ? window.innerHeight : this.el.$win.height()
    };

    // ターゲットコンテンツサイズ
    const targetSize: {width: number, height: number} = {
      width: this.el.$target.outerWidth(false),
      height: this.el.$target.height()
    };

    // ターゲットコンテンツサイズがウィンドウサイズより大きいか判定
    const over: {width: boolean, height: boolean} = {
      width: targetSize.width > winSize.width - this.setting.padding * 2,
      height: targetSize.height > winSize.height - this.setting.padding * 2
    };

    // ターゲットコンテンツサイズを設定し直す
    targetSize.width = (! over.width) ? targetSize.width : winSize.width - this.setting.padding * 2;
    targetSize.height = (! over.height) ? targetSize.height : winSize.height - this.setting.padding * 10;

    // 閉じるボタンのサイズ
    const closeSize: {width: number, height: number} = {
      width: this.el.$close.outerWidth(true),
      height: this.el.$close.outerHeight(true)
    };

    // 位置を算出
    const position: {left: number, top: number} = {
      left: (winSize.width / 2) - (targetSize.width / 2),
      top: this.el.$win.scrollTop()
        // + ((winSize.height / 2) - (targetSize.height / 2))
    };

    // ターゲットコンテンツ
    this.el.$target.css({
      'left': position.left,
      'top': position.top
    });
  }

  /**
   * イベントセット
   */
  private setEvent(): void {
    let resize: any = 0;

    // モーダルリンク
    this.el.$root.on('click', (event: JQueryEventObject): void => {
      // event.stopPropagation();
      event.preventDefault();
      this.onModalOpen(event);
    });

    // レイヤー
    this.el.$layer.on('click', (event: JQueryEventObject): void => {
      this.onModalClose(event);
    });

    // ターゲットコンテンツ内のクリックはバブリングさせない
    this.el.$targetInner.on('click', (event: JQueryEventObject): void => {
      event.stopPropagation();
    });

    // 閉じるボタン
    this.el.$close.on('click', (event: JQueryEventObject): void => {
      event.stopPropagation();
      this.onModalClose(event);
    });

    // Escキー
    this.el.$close.on('keyup', (event: JQueryEventObject): void => {
      // ターゲットが表示時のみ動作させる
      if (! this.state.isOpen) {
        return;
      }

      if (event.keyCode === 27) {
        this.onModalClose(event);
      }
    });

    this.el.$win.on('resize', (): void => {
      clearTimeout(resize);
      resize = setTimeout((): void => {
        this.adjustStyle();
      }, 80);
    });
  }
}
